/*
  Reia: Live automatic-discount preview for PDP.

  Automatic/order discounts (e.g. "Rakshabandhan") are only computed by Shopify
  once a variant is in the cart - they're not part of the static product/variant
  price object Liquid renders on the PDP. To show the same discount name +
  amount the cart drawer shows, this silently previews the selected variant
  through the real cart engine (add it, read what Shopify actually applied,
  then remove it again) and renders the result using the same markup/classes
  as the cart drawer's discount badge, so it's pixel-identical.

  If the variant is already in the customer's cart, no add/remove round trip
  happens - the existing line's real discount data is read directly instead.

  Self-healing: the preview add is tagged with a hidden line-item property
  (PREVIEW_PROPERTY_KEY). If a page reload interrupts the cycle between the
  add and the revert-to-zero (fetch() requests are aborted on navigation
  unless kept alive), the tagged item is left sitting in the real cart. Every
  preview run sweeps the whole cart for any such tagged item and reverts it,
  so a stuck item self-heals on the very next page load anywhere on the site
  rather than persisting until the customer notices it at checkout.
*/
(function () {
  'use strict';

  var PREVIEW_PROPERTY_KEY = '_reia_discount_preview';

  function isPreviewGhost(item) {
    return !!(item && item.properties && item.properties[PREVIEW_PROPERTY_KEY]);
  }

  function buildPreviewProperties() {
    var properties = {};
    properties[PREVIEW_PROPERTY_KEY] = 'true';
    return properties;
  }

  var container = document.querySelector('.product[data-is-full-product="true"]');
  if (!container) return;

  // Badge slots aren't only in the main price block - the Price Details tab
  // (a separate section, not nested inside .product) has its own copy too, so
  // these are queried page-wide rather than scoped to container.
  var badgeEls = document.querySelectorAll('[data-reia-live-discount]');
  if (!badgeEls.length) return;

  var priceEls = container.querySelectorAll('[data-price]');
  var comparePriceEls = container.querySelectorAll('[data-compare-price]');
  if (!priceEls.length) return;

  // Reia: the "(-$XXX)" amount inside both discount badges below is always
  // suppressed - only the discount title/name shows (e.g. "Rakshabandhan
  // Sale"). Price and compare-at price are untouched by this; it only
  // affects the badges' own amount text.
  var hideDiscountAmount = true;

  var routes = (window.theme && window.theme.routes && window.theme.routes.cart) || {
    add: '/cart/add',
    change: '/cart/change',
    base: '/cart'
  };
  var cartAddUrl = routes.add + '.js';
  var cartChangeUrl = routes.change + '.js';
  var cartUrl = routes.base + '.js';
  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatMoney(cents) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholderRegex);
    if (!match) return String(cents);

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return moneyFormat.replace(placeholderRegex, value);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  var DISCOUNT_BADGE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="currentColor" d="M10.63 22.82 1.2 13.34A4 4 0 0 1 0 10.53V4a4.11 4.11 0 0 1 .3-1.52 3.81 3.81 0 0 1 .87-1.3A3.88 3.88 0 0 1 2.48.3 4 4 0 0 1 4 0l6.73.05a4 4 0 0 1 2.79 1.17l9.29 9.34A4 4 0 0 1 24 13.4a4 4 0 0 1-1.17 2.83l-6.56 6.59a3.87 3.87 0 0 1-1.29.87 3.81 3.81 0 0 1-1.53.31 4 4 0 0 1-2.82-1.18zM7.3 7.43a1.55 1.55 0 0 0 0-2.17 1.53 1.53 0 1 0 0 2.17z"/>' +
    '</svg>';

  function setBadgeEls(els, html) {
    els.forEach(function (el) {
      el.innerHTML = html;
      el.hidden = !html;
    });
  }

  // No live automatic discount applies to this variant - restore the SSR-rendered
  // native price/compare_at_price (which already correctly reflects a plain
  // Shopify sale price, if this variant has one) instead of blanking it.
  function resetToNativePrice(variant) {
    setBadgeEls(badgeEls, '');

    var hasNativeCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    comparePriceEls.forEach(function (el) {
      el.innerHTML = hasNativeCompare ? formatMoney(variant.compare_at_price) : '';
    });
    priceEls.forEach(function (el) {
      el.innerHTML = formatMoney(variant.price);
    });
  }

  function renderDiscountUI(unitOriginalPrice, unitFinalPrice, discountEntries, variant) {
    if (!discountEntries.length || unitFinalPrice >= unitOriginalPrice) {
      resetToNativePrice(variant);
      return;
    }

    comparePriceEls.forEach(function (el) {
      el.innerHTML = formatMoney(unitOriginalPrice);
    });
    priceEls.forEach(function (el) {
      el.innerHTML = formatMoney(unitFinalPrice);
    });

    var badgeHtml = discountEntries
      .map(function (entry) {
        var amountHtml = hideDiscountAmount
          ? ''
          : ' <span style="white-space:nowrap">(-' + formatMoney(entry.amount) + ')</span>';
        return (
          '<li class="quick-cart__item-discount fs-body-60">' +
          '<span class="icon icon-new icon-discount-badge">' +
          DISCOUNT_BADGE_ICON +
          '</span>' +
          escapeHtml(entry.title) +
          amountHtml +
          '</li>'
        );
      })
      .join('');
    setBadgeEls(badgeEls, badgeHtml);
  }

  function revertGhostItem(item) {
    // Target by line key, not variant id - a customer can have a genuine
    // cart line for this same variant (different properties) coexisting
    // with our tagged ghost line, and variant id alone can't tell them
    // apart. keepalive lets this still finish sending even if the page is
    // mid-navigation when it fires - the whole reason ghosts get stuck.
    fetch(cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
      keepalive: true
    }).catch(function () {});
  }

  var requestToken = 0;

  function previewVariant(variant) {
    var token = ++requestToken;
    var variantId = variant.id;

    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (token !== requestToken) return null;

        var items = cart.items || [];

        // Self-heal: clean up any of our own preview adds left stuck by an
        // interrupted reload, regardless of which variant they're on.
        var ghosts = items.filter(isPreviewGhost);
        ghosts.forEach(revertGhostItem);

        // A ghost line for the variant we're about to preview doesn't count
        // as "already in cart" - skip this cycle's preview rather than race
        // the cleanup above with a fresh add; it'll resolve on the next load.
        var currentVariantHasGhost = ghosts.some(function (item) {
          return item.variant_id === variantId;
        });
        if (currentVariantHasGhost) {
          resetToNativePrice(variant);
          return null;
        }

        var existingLine = items.find(function (item) {
          return item.variant_id === variantId && !isPreviewGhost(item);
        });

        if (existingLine) {
          var qty = existingLine.quantity || 1;
          var entries = (existingLine.discounts || []).map(function (d) {
            return { title: d.title, amount: Math.round(d.amount / qty) };
          });
          renderDiscountUI(existingLine.original_price, existingLine.final_price, entries, variant);
          return null;
        }

        return fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: buildPreviewProperties()
          })
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (addedLine) {
            var entries = (addedLine.discounts || []).map(function (d) {
              return { title: d.title, amount: d.amount };
            });
            if (token === requestToken) {
              renderDiscountUI(addedLine.original_price || variant.price, addedLine.final_price, entries, variant);
            }
            // Target by line key (not variant id) for the same reason as
            // revertGhostItem above. keepalive so this still fires even if
            // the customer reloads/navigates right after the add resolves -
            // if it doesn't, the sweep at the top of the next preview cycle
            // (anywhere on the site) cleans it up instead.
            return fetch(cartChangeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: addedLine.key, quantity: 0 }),
              keepalive: true
            });
          });
      })
      .catch(function () {
        if (token === requestToken) resetToNativePrice(variant);
      });
  }

  function runPreviewFromVariant(variant) {
    if (!variant || !variant.available) {
      setBadgeEls(badgeEls, '');
      return;
    }
    previewVariant(variant);
  }

  // Manual discount badge (custom.discount_badge metafield) - static admin data,
  // no cart preview needed. Used for products with no live automatic discount.
  // Same page-wide scoping as badgeEls above (Price Details tab has its own slot).
  var metafieldBadgeEls = document.querySelectorAll('[data-reia-metafield-discount-badge]');
  var metafieldBadgeDataEl = container.querySelector('[data-reia-metafield-badges]');
  var metafieldBadges = {};

  if (metafieldBadgeEls.length && metafieldBadgeDataEl) {
    try {
      (JSON.parse(metafieldBadgeDataEl.textContent) || []).forEach(function (entry) {
        metafieldBadges[entry.id] = entry;
      });
    } catch (e) {
      metafieldBadges = {};
    }
  }

  function renderMetafieldBadge(variant) {
    if (!metafieldBadgeEls.length) return;
    var entry = variant && metafieldBadges[String(variant.id)];
    if (!entry) {
      setBadgeEls(metafieldBadgeEls, '');
      return;
    }

    var iconHtml = entry.icon
      ? '<span class="icon icon-new icon-discount-badge"><img src="' + entry.icon + '" alt="" width="32" height="32" loading="lazy"></span>'
      : '<span class="icon icon-new icon-discount-badge">' + DISCOUNT_BADGE_ICON + '</span>';
    var amountHtml =
      entry.amount && !hideDiscountAmount
        ? ' <span style="white-space:nowrap">(-' + formatMoney(entry.amount) + ')</span>'
        : '';

    setBadgeEls(
      metafieldBadgeEls,
      '<li class="quick-cart__item-discount fs-body-60">' + iconHtml + escapeHtml(entry.name) + amountHtml + '</li>'
    );
  }

  // Initial variant on page load
  var sectionId = container.dataset.sectionId;
  var productJsonEl = sectionId && document.getElementById('ProductJson-' + sectionId);
  var productData = null;
  if (productJsonEl) {
    try {
      productData = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      productData = null;
    }
  }

  if (productData && container.dataset.currentProductId) {
    var initialId = parseInt(container.dataset.currentProductId, 10);
    var initialVariant = productData.variants.find(function (v) {
      return v.id === initialId;
    });
    if (initialVariant) {
      runPreviewFromVariant(initialVariant);
      renderMetafieldBadge(initialVariant);
    }
  }

  // Subsequent variant swaps (swatches, size selectors, etc.)
  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    runPreviewFromVariant(variant);
    renderMetafieldBadge(variant);
  });
})();
